<template>
  <div class="gift-wrap">
    <div class="gift-head">
      <h1 class="gift-title">Canjear regalo</h1>
      <p class="gift-sub">
        Pide el código al cliente, escribe el monto de su boleta y canjea.
        Cada código sirve una sola vez.
      </p>
    </div>

    <form class="gift-card" @submit.prevent="redeem">
      <div class="gift-field">
        <label for="gift-code">Código del cliente</label>
        <input
          id="gift-code"
          ref="codeInput"
          v-model="code"
          class="gift-input gift-code"
          type="text"
          placeholder="KMT482"
          autocomplete="off"
          spellcheck="false"
          :disabled="sending"
        >
      </div>

      <div class="gift-field">
        <label for="gift-amount">Monto de la boleta (S/)</label>
        <input
          id="gift-amount"
          v-model="purchaseAmount"
          class="gift-input"
          type="number"
          step="0.10"
          min="0"
          placeholder="0.00"
          :disabled="sending"
        >
      </div>

      <div v-if="lookingUp" class="gift-field gift-lookup">Buscando el regalo de este código…</div>
      <div v-else-if="lookupMessage" class="gift-field gift-lookup gift-lookup-bad">{{ lookupMessage }}</div>
      <div v-else-if="options.length === 1" class="gift-field">
        <label>Regalo</label>
        <div class="gift-fixed">{{ options[0].name }}</div>
        <p class="gift-hint">{{ giftName }} · consumo mínimo S/{{ toSoles(minimumPurchaseCents) }}</p>
      </div>
      <div v-else-if="options.length > 1" class="gift-field">
        <label for="gift-meal">Regalo, elige qué le entregas</label>
        <select id="gift-meal" v-model="selectedMealId" class="gift-input gift-select" :disabled="sending">
          <option :value="null" disabled>Elige una opción</option>
          <option v-for="option in options" :key="option.id" :value="option.id">{{ option.name }}</option>
        </select>
        <p class="gift-hint">{{ giftName }} · consumo mínimo S/{{ toSoles(minimumPurchaseCents) }}</p>
      </div>

      <button class="gift-button" type="submit" :disabled="sending || !canSubmit">
        {{ sending ? 'Canjeando…' : 'Canjear' }}
      </button>
    </form>

    <div v-if="accepted" class="gift-result gift-ok">
      <p class="gift-result-title">Canje registrado</p>
      <p>
        Entrega <strong>{{ accepted.delivered_meal }}</strong>.
        Código {{ accepted.formatted_code }}, boleta S/{{ toSoles(accepted.purchase_amount_cents) }}.
      </p>
      <p class="gift-result-origin">Vino de {{ accepted.campaign_name }}.
      </p>
    </div>

    <div v-if="rejection" class="gift-result gift-bad">
      <p class="gift-result-title">No se puede canjear</p>
      <p>{{ rejection }}</p>
    </div>

    <redemptions-of-day ref="ofDay"></redemptions-of-day>
  </div>
</template>

<script>
import GiftCodesGateway from '../../gateways/GiftCodesGateway'
import RedemptionsOfDay from './RedemptionsOfDay'

const CENTS_PER_SOL = 100
const DECIMALS = 2

const CODE_LENGTH = 6
const NO_LOOKUP_YET = 0

const MESSAGE_BY_REASON = {
  NOT_FOUND: 'Ese código no existe. Revisa que esté bien escrito.',
  ALREADY_REDEEMED: 'Ese código ya fue canjeado antes.',
  EXPIRED: 'Ese código ya venció.',
  PURCHASE_BELOW_MINIMUM: 'La boleta no llega al consumo mínimo del regalo.',
  GIFT_NOT_AVAILABLE_HERE: 'Esta tienda no tiene en carta el regalo de este código.',
  MEAL_REQUIRED: 'Elige qué postre le entregas.',
  MEAL_NOT_ALLOWED: 'Ese postre no está habilitado para este código.',
  CAMPAIGN_CLOSED: 'Esa campaña fue cerrada, ese código ya no se puede canjear.',
  WITHOUT_CATERING: 'Tu usuario no tiene una tienda asociada, avisa a soporte.'
}
const FALLBACK_MESSAGE = 'No se pudo canjear. Intenta de nuevo en un momento.'

export default {
  name: 'RedeemGift',
  components: { RedemptionsOfDay },
  data () {
    return {
      code: '',
      purchaseAmount: '',
      options: [],
      giftName: '',
      minimumPurchaseCents: 0,
      selectedMealId: null,
      lookingUp: false,
      lookupMessage: null,
      lastLookup: NO_LOOKUP_YET,
      sending: false,
      accepted: null,
      rejection: null
    }
  },
  computed: {
    normalizedCode () {
      return this.code.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
    },
    canSubmit () {
      return this.options.length > 0 && this.purchaseAmount !== '' &&
        (this.options.length === 1 || this.selectedMealId !== null)
    }
  },
  watch: {
    normalizedCode (value) {
      this.lastLookup += 1
      this.lookingUp = false
      this.options = []
      this.giftName = ''
      this.minimumPurchaseCents = 0
      this.selectedMealId = null
      this.lookupMessage = null
      if (value.length === CODE_LENGTH) {
        this.lookupOptions(value)
      }
    }
  },
  methods: {
    toSoles (cents) {
      return ((cents || 0) / CENTS_PER_SOL).toFixed(DECIMALS)
    },
    toCents (amount) {
      return Math.round(parseFloat(amount) * CENTS_PER_SOL)
    },
    redeem () {
      this.sending = true
      this.accepted = null
      this.rejection = null

      const mealId = this.options.length === 1 ? this.options[0].id : this.selectedMealId

      GiftCodesGateway.redeem(this.normalizedCode, this.toCents(this.purchaseAmount), mealId)
        .then(response => {
          this.accepted = response.data.payload.results
          this.code = ''
          this.purchaseAmount = ''
          this.$refs.ofDay.reloadToday()
        })
        .catch(error => {
          this.rejection = this.messageFor(error)
        })
        .then(() => {
          this.sending = false
          this.$refs.codeInput.focus()
        })
    },
    lookupOptions (code) {
      this.lastLookup += 1
      const lookup = this.lastLookup
      this.lookingUp = true
      GiftCodesGateway.options(code)
        .then(response => {
          if (lookup !== this.lastLookup) {
            return
          }
          const results = response.data.payload.results
          this.giftName = results.gift_name
          this.minimumPurchaseCents = results.minimum_purchase_cents
          this.options = results.options
        })
        .catch(error => {
          if (lookup === this.lastLookup) {
            this.lookupMessage = this.messageFor(error)
          }
        })
        .then(() => {
          if (lookup === this.lastLookup) {
            this.lookingUp = false
          }
        })
    },
    messageFor (error) {
      const payload = ((error.response || {}).data || {}).payload || {}

      return MESSAGE_BY_REASON[payload.reason] || FALLBACK_MESSAGE
    }
  }
}
</script>

<style scoped>
.gift-wrap { max-width: 760px; margin: 0 auto; padding: 22px; color: #1a2420; font-size: 15px; }
.gift-head { margin-bottom: 18px; }
.gift-title { font-size: 21px; font-weight: 720; margin: 0; }
.gift-sub { color: #5c6b63; font-size: 12.5px; margin: 2px 0 0; line-height: 1.55; }
.gift-card { background: #fff; border: 1px solid #e2e8e3; border-radius: 14px; padding: 16px 18px; box-shadow: 0 4px 16px rgba(26,36,32,.04); }
.gift-field { margin-bottom: 16px; }
.gift-field label { font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .06em; color: #8a988f; display: block; margin-bottom: 6px; }
.gift-input { width: 100%; box-sizing: border-box; background: #f0f4f1; border: 1.5px solid #e2e8e3; color: #1a2420; font: inherit; font-weight: 600; padding: 10px 14px; border-radius: 10px; }
.gift-input:focus { outline: none; border-color: #5DBC85; }
.gift-code { font-family: monospace; font-size: 20px; letter-spacing: 2px; text-transform: uppercase; }
.gift-select { appearance: none; cursor: pointer; padding-right: 38px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath fill='%235c6b63' d='M1.4 0 6 4.6 10.6 0 12 1.4 6 7.4 0 1.4z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; background-size: 11px; }
.gift-fixed { background: #f0f4f1; border: 1.5px solid #e2e8e3; color: #1a2420; font-weight: 600; padding: 10px 14px; border-radius: 10px; }
.gift-lookup { font-size: 13px; color: #5c6b63; }
.gift-lookup-bad { color: #f5365c; }
.gift-hint { margin: 6px 0 0; font-size: 12px; color: #8a988f; }
.gift-result-origin { margin: 6px 0 0; font-size: 12.5px; color: #5c6b63; }
.gift-button { width: 100%; background: #5DBC85; border: 1.5px solid #5DBC85; color: #fff; font: inherit; font-weight: 700; font-size: 15px; padding: 11px 14px; border-radius: 9px; cursor: pointer; }
.gift-button:disabled { background: #f0f4f1; border-color: #e2e8e3; color: #8a988f; cursor: not-allowed; }
.gift-result { margin-top: 16px; padding: 14px 16px; border-radius: 14px; font-size: 14px; border: 1px solid #e2e8e3; background: #fff; box-shadow: 0 4px 16px rgba(26,36,32,.04); }
.gift-result-title { margin: 0 0 4px; font-weight: 700; }
.gift-ok { border-color: rgba(93,188,133,.35); background: rgba(93,188,133,.08); }
.gift-ok .gift-result-title { color: #3d9e68; }
.gift-bad { border-color: rgba(245,54,92,.3); background: rgba(245,54,92,.06); }
.gift-bad .gift-result-title { color: #f5365c; }
</style>
